/**
 * InstallGuide (SK-INSTALL, T-01) — the gear-menu walkthrough for adding Mark
 * to the home screen (§8.3).
 *
 * PLATFORM-BRANCHED (task #6, 2026-07-27). This screen used to render iOS
 * Safari's manual Share → Add to Home Screen flow unconditionally, on the
 * assumption that Android never needs it ("Android Chrome installs via the
 * manifest+SW auto-prompt"). That assumption does not survive contact with the
 * field: the auto-prompt is one-time and dismissible, Chrome suppresses it under
 * its own engagement heuristics, and the pilot device is a Pixel. A super who
 * opened this screen on Android was told to find a Share icon in Safari — every
 * instruction wrong for the device in their hand, on the screen whose whole job
 * is getting the app installed.
 *
 * Three paths now:
 *   * Android with a live `beforeinstallprompt` → a real Install button that
 *     fires the browser's own prompt (`onInstall`). One tap, no narration.
 *   * Android without one (already dismissed, or Chrome withheld it) → the
 *     genuine Chrome menu steps.
 *   * iOS Safari → the manual Share → Add to Home Screen steps (IosInstallSteps,
 *     shared with the iOS install gate).
 * Anything else (desktop, unknown UA) gets the honest generic line rather than a
 * confidently wrong OS-specific script — rule #1's calm-and-correct over
 * plausible-looking filler.
 */

import { Chip } from "./Chip";
import { IosInstallSteps } from "./IosInstallSteps";

export type InstallPlatform = "ios" | "android" | "other";

export interface InstallGuideProps {
  onDismiss?: () => void;
  /** Fires the browser's own install prompt (the stashed
   * `beforeinstallprompt`). Absent when no prompt is available — the guide then
   * narrates the manual path instead of showing a button that would do nothing
   * (the app's own "never a dead tap" rule). */
  onInstall?: () => void;
  /** Test seam: override UA sniffing. Production callers omit it. */
  platform?: InstallPlatform;
}

/** Best-effort platform read. iPadOS 13+ reports a desktop-Safari UA, so the
 * touch-capable-Mac check is the standard fallback for it. */
export function detectPlatform(): InstallPlatform {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  if (/Macintosh/.test(ua) && typeof document !== "undefined" && "ontouchend" in document) return "ios";
  if (/Android/.test(ua)) return "android";
  return "other";
}

export function InstallGuide(props: InstallGuideProps) {
  const platform = props.platform ?? detectPlatform();

  return (
    <section data-skin-slot="SK-INSTALL" data-testid="install-guide" data-platform={platform}>
      <h1>Install Mark</h1>
      <p>
        Mark works best installed to your home screen — it opens full-screen, works offline, and
        keeps your capture queue safe between sessions.
      </p>

      {platform === "ios" && (
        <>
          <p data-testid="install-guide-ios-badge">iPhone/iPad detected.</p>
          <IosInstallSteps />
        </>
      )}

      {platform === "android" && (
        <>
          <p data-testid="install-guide-android-badge">Android detected.</p>
          {props.onInstall ? (
            // Chrome has an install prompt ready — one tap beats four steps.
            <>
              <p>Tap below and confirm — Chrome will add Mark to your home screen.</p>
              <Chip
                data-testid="install-guide-install"
                onClick={props.onInstall}
                style="min-height:44px"
              >
                Install Mark
              </Chip>
            </>
          ) : (
            <ol data-testid="install-guide-steps">
              <li>Open this page in Chrome.</li>
              <li>
                Tap the <strong>⋮</strong> menu in the top-right corner.
              </li>
              <li>
                Tap <strong>Install app</strong> — or <strong>Add to Home screen</strong> if you
                don't see it.
              </li>
              <li>
                Tap <strong>Install</strong> to confirm.
              </li>
            </ol>
          )}
        </>
      )}

      {platform === "other" && (
        // No confidently-wrong script for a device we can't identify.
        <p data-testid="install-guide-generic">
          Open Mark on your phone to install it — your browser's menu will offer{" "}
          <strong>Install</strong> or <strong>Add to Home Screen</strong>.
        </p>
      )}

      <p>Mark will now appear as an app icon on your home screen.</p>
      {props.onDismiss && (
        <Chip data-testid="install-guide-dismiss" onClick={props.onDismiss}>
          Done
        </Chip>
      )}
    </section>
  );
}
